import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import type { JsonLdTemplateType, PushToWordpressRequest } from "shared";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { useCompany } from "../company/useCompany";
import { QueryKeys } from "../LinkGen";

export const useSeoAudit = () => {
	const { id: companyId } = useCompany();
	return useSuspenseQuery({
		queryKey: QueryKeys.Seo.Audit(companyId),
		queryFn: async () => {
			const res = await Api.GetSeoAudit();
			return res.data;
		},
	});
};

export const useSeoAuditActions = () => {
	const queryClient = useQueryClient();
	const emit = useAlertEmit();
	const { id: companyId } = useCompany();

	const runAudit = useMutation({
		mutationFn: async (websiteUrl: string) => {
			const res = await Api.RunSeoAudit({ websiteUrl });
			return res.data;
		},
		onSuccess: async () => {
			await queryClient.refetchQueries({
				queryKey: QueryKeys.Seo.Audit(companyId),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "SEO audit completed successfully.",
			});
		},
		onError: (e: unknown) => {
			if (isAxiosError(e)) {
				emit({
					type: AlertType.ERROR,
					content:
						e.response?.data?.message ||
						"Failed to run SEO audit. Please try again.",
				});
				return;
			}
			emit({
				type: AlertType.ERROR,
				content: "Failed to run SEO audit. Please try again.",
			});
		},
	});

	const generateJsonLd = useMutation({
		mutationFn: async (templateType: JsonLdTemplateType) => {
			const res = await Api.GenerateJsonLd({ templateType });
			return res.data;
		},
		onSuccess: () => {
			emit({
				type: AlertType.SUCCESS,
				content: "Schema generated successfully.",
			});
		},
		onError: (e: unknown) => {
			if (isAxiosError(e)) {
				emit({
					type: AlertType.ERROR,
					content:
						e.response?.data?.message ||
						"Failed to generate JSON-LD template. Please try again.",
				});
				return;
			}
			emit({
				type: AlertType.ERROR,
				content: "Failed to generate JSON-LD template. Please try again.",
			});
		},
	});

	const pushToWordpress = useMutation({
		mutationFn: async (dto: PushToWordpressRequest) => {
			const res = await Api.PushJsonLdToWordpress(dto);
			return res.data;
		},
		onSuccess: () => {
			emit({
				type: AlertType.SUCCESS,
				content: "Script pushed to WordPress successfully.",
			});
		},
		onError: (e: unknown) => {
			if (isAxiosError(e)) {
				emit({
					type: AlertType.ERROR,
					content:
						e.response?.data?.message ||
						"Failed to push to WordPress. Make sure the Firestarter SEO plugin is installed and activated.",
				});
				return;
			}
			emit({
				type: AlertType.ERROR,
				content:
					"Failed to push to WordPress. Make sure the Firestarter SEO plugin is installed and activated.",
			});
		},
	});

	return { runAudit, generateJsonLd, pushToWordpress };
};
